import * as React from 'react';
import { MenuItem, Select, TextField } from '@mui/material';
import { Period } from './types';

const PERIODS = [7, 30, 90];
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
// The Pacific date before `date` (YYYY-MM-DD), counted at noon so no zone shift crosses a day.
const dayBefore = (date: string) => new Date(Date.parse(`${date}T12:00:00Z`) - 86_400_000).toISOString().slice(0, 10);

// What the period menu shows: a number of days, today, yesterday, or a range.
type PeriodChoice = number | 'today' | 'yesterday' | 'range';
const singleDay = (choice: 'today' | 'yesterday') => {
  const date = choice === 'today' ? todayPacific() : dayBefore(todayPacific());
  return { from: date, to: date };
};
// The menu entry a period shows as: a single day that is today or yesterday is that entry, not a range.
const choiceOf = (period: Period): PeriodChoice => {
  if ('days' in period) return period.days;
  if (period.from !== period.to) return 'range';
  const today = todayPacific();
  return period.from === today ? 'today' : period.from === dayBefore(today) ? 'yesterday' : 'range';
};

// Today or yesterday (Pacific), the last 7/30/90 days, or a range of dates:
// the chart and the tables follow it.
export function PeriodPicker({ period, onChange, name = 'period', labelId = 'post-purchase-period-label' }: { period: Period; onChange: (period: Period) => void; name?: string; labelId?: string }) {
  const [choice, setChoice] = React.useState<PeriodChoice>(() => choiceOf(period));
  const [range, setRange] = React.useState(() => {
    const to = todayPacific();
    const from = new Date(Date.parse(`${to}T12:00:00Z`) - 13 * 86_400_000).toISOString().slice(0, 10);
    return 'days' in period ? { from, to } : { from: period.from, to: period.to };
  });
  const apply = (next: { from: string; to: string }) => {
    setRange(next);
    if (next.from && next.to && next.from <= next.to) onChange(next);
  };
  const choose = (next: PeriodChoice) => {
    setChoice(next);
    if (next === 'range') apply(range);
    else if (next === 'today' || next === 'yesterday') onChange(singleDay(next));
    else onChange({ days: next });
  };
  return (
    <>
      <Select
        size="small"
        value={choice}
        onChange={event => choose(event.target.value as PeriodChoice)}
        labelId={labelId}
        name={name}
      >
        <MenuItem value="today">Today</MenuItem>
        <MenuItem value="yesterday">Yesterday</MenuItem>
        {PERIODS.map(days => (
          <MenuItem key={days} value={days}>Last {days} days</MenuItem>
        ))}
        <MenuItem value="range">Date range…</MenuItem>
      </Select>
      {choice === 'range' && (
        <>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={range.from}
            inputProps={{ max: range.to }} onChange={event => apply({ ...range, from: event.target.value })} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={range.to}
            inputProps={{ min: range.from, max: todayPacific() }} onChange={event => apply({ ...range, to: event.target.value })} />
        </>
      )}
    </>
  );
}
